import type {CSSProperties} from 'react';
import {wedgeExactRole} from './wedgeRoles.js';

export type WedgeRepXpDeskLook = 'repxpd-tab' | 'repxpd-pill' | 'repxpd-round' | 'repxpd-row' | 'repxpd-town'
  | 'repxpd-guide' | 'repxpd-coach' | 'repxpd-tile' | 'repxpd-outcome' | 'repxpd-sheetrow';

export const isWedgeRepXpDeskLook = (look: string | undefined): look is WedgeRepXpDeskLook => !!look && look.startsWith('repxpd-');

/* Released desktop Rep XP page (`RepXpDesktopV2`): five bare controls on a page that paints its own palette. The palette is chosen by the page from its
   theme prop and handed to the caller, so no look draws a color: the caller passes the ink, the fill and the rule in its style.
   `repxpd-tab` is a filter tab: a transparent inline flex row with an 8px gap, no border, padding only below (12px), 11px heavy uppercase type in the
   display face with 1.4px of tracking. The caller passes the ink and the bottom rule (2px, title ink on the chosen tab, transparent otherwise).
   `repxpd-pill` is the 32px capsule (Sort, Route all, Open in Drive): 0 by 12px padding, 13px semibold type in the body face. The caller passes the
   half-pixel rule, the ink and the fill; the solid Route all pill also passes 14px side padding and bold type.
   `repxpd-round` is the 38px round header control (Sync, Notifications). The caller passes the rule, the fill and the ink.
   `repxpd-row` is a shortlist line: a full-width left-aligned flex row with a 14px gap and 14px by 2px padding; the caller passes the bottom rule.
   `repxpd-town` is a town line of the book: the same full-width row with a 12px gap and 9px by 2px padding, no rule.
   The caller owns the labels, the glyphs, the handlers and the flex shrink. No look draws a transparent target and no anchor
   (wedgeSettingsDrawsHit): the legacy geometry stays (owner ruling SE-6a and SE-6b), the pills sit 8px apart in a 32px row, and the lines are full-width
   rows that touch each other. */
/* Phone and iPad Rep XP hub (`RepXpDashboard`): five more bare controls, in the same family because the same hit rules skip them.
   `repxpd-guide` is the full-width "Levels and how to rank up" button: a centered flex row with an 8px gap, 11px by 14px padding, a 12px radius,
   the raised fill with the card rule, 13px bold type in the page ink. The caller passes the top margin.
   `repxpd-coach` is a coaching card: a full-width left-aligned flex row, 11px gap, 13px by 14px padding, 14px radius. The caller passes the tint,
   the rule, the shadow and the transition, because they follow the tone of the card.
   `repxpd-tile` is the transparent shell around a record tile: a flex box with no border and no padding. The caller passes the flex basis.
   `repxpd-outcome` is an outcome count tile: a flexible left-aligned box with 10px by 12px padding and a 12px radius. The caller passes the tint,
   the rule and the shadow.
   `repxpd-sheetrow` is an account line of the outcome list: a full-width space-between flex row, 10px gap, 12px by 14px padding, the raised fill with
   the card rule. Each inherits the font of its row first, as the source does, then sets the body face. No look draws a transparent target and no
   anchor (legacy geometry, SE-6a and SE-6b). */
export function wedgeRepXpDeskStyle(look: WedgeRepXpDeskLook): CSSProperties {
  const body = wedgeExactRole('font');
  if (look === 'repxpd-guide') return {
    width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '11px 14px', borderRadius: 12,
    background: wedgeExactRole('bgElev'), border: `1px solid ${wedgeExactRole('cardBorder')}`, cursor: 'pointer', font: 'inherit', fontFamily: body,
    fontSize: 13, fontWeight: 700, color: wedgeExactRole('label'),
  };
  if (look === 'repxpd-coach') return {
    display: 'flex', alignItems: 'flex-start', gap: 11, width: '100%', textAlign: 'left', borderRadius: 14, padding: '13px 14px', cursor: 'pointer',
    font: 'inherit', fontFamily: body,
  };
  if (look === 'repxpd-tile') return {
    display: 'flex', background: 'transparent', border: 0, padding: 0, textAlign: 'inherit', cursor: 'pointer', fontFamily: body,
  };
  if (look === 'repxpd-outcome') return {
    flex: 1, minWidth: 0, borderRadius: 12, padding: '10px 12px', cursor: 'pointer', font: 'inherit', fontFamily: body, textAlign: 'left',
  };
  if (look === 'repxpd-sheetrow') return {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, width: '100%', padding: '12px 14px', borderRadius: 12,
    background: wedgeExactRole('bgElev'), border: `1px solid ${wedgeExactRole('cardBorder')}`, cursor: 'pointer', font: 'inherit', fontFamily: body, textAlign: 'left',
  };
  if (look === 'repxpd-tab') return {
    background: 'transparent', border: 0, cursor: 'pointer', padding: '0 0 12px',
    fontFamily: wedgeExactRole('fontDisplay'), fontSize: 11, fontWeight: 800, letterSpacing: 1.4, textTransform: 'uppercase',
    display: 'inline-flex', alignItems: 'center', gap: 8,
  };
  if (look === 'repxpd-pill') return {
    height: 32, padding: '0 12px', borderRadius: 999,
    fontFamily: body, fontSize: 13, fontWeight: 600, cursor: 'pointer',
  };
  if (look === 'repxpd-round') return {width: 38, height: 38, borderRadius: 999, cursor: 'pointer'};
  if (look === 'repxpd-row') return {
    display: 'flex', alignItems: 'center', gap: 14, width: '100%', textAlign: 'left', cursor: 'pointer', padding: '14px 2px',
    background: 'transparent', border: 0, fontFamily: body,
  };
  return {
    display: 'flex', alignItems: 'center', gap: 12, padding: '9px 2px', background: 'transparent', border: 0, cursor: 'pointer',
    width: '100%', textAlign: 'left', fontFamily: body,
  };
}
